/**
 * @fileoverview CashFlowChart - Bar chart with cumulative cash line overlay
 *
 * Purpose: Display net cash change per period with cumulative trend.
 * Features polished styling: rounded bars, subtle grid, styled dots.
 *
 * Used by: Review phase cash flow analysis
 */

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import {
  periodAlignedLayout,
  type PeriodAlignedChartProps,
} from "./periodAlignedLayout";

const config = {
  netCashChange: { label: "Net Cash Change", color: "hsl(var(--chart-1))" },
  cumulativeCashChange: {
    label: "Cumulative",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const dollarFormatter = (v: number) => `$${(v / 1000).toFixed(0)}k`;

export interface CashFlowChartProps extends PeriodAlignedChartProps {
  periods: string[];
  netCashChange: (number | null)[];
  cumulativeCashChange: (number | null)[];
}

/** Net cash change bars per period with a cumulative line; nulls leave gaps. */
export function CashFlowChart({
  periods,
  netCashChange,
  cumulativeCashChange,
  plotInsetLeft,
}: CashFlowChartProps) {
  const layout = periodAlignedLayout(plotInsetLeft, 60);
  // Missing values stay null — a bar is skipped and a line breaks — rather
  // than plotting as zero beside a table that shows a dash (MOR-1282 QA).
  const data = periods.map((period, i) => ({
    period,
    netCashChange: netCashChange[i] ?? null,
    cumulativeCashChange: cumulativeCashChange[i] ?? null,
  }));

  return (
    <ChartContainer config={config} className="min-h-[180px] w-full">
      <ComposedChart data={data} margin={layout.margin}>
        <CartesianGrid
          vertical={false}
          stroke="hsl(var(--border))"
          strokeOpacity={0.5}
        />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickFormatter={dollarFormatter}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          tickLine={false}
          axisLine={false}
          width={layout.yAxisWidth}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(v) =>
                typeof v === "number" ? dollarFormatter(v) : "—"
              }
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar
          dataKey="netCashChange"
          fill="var(--color-netCashChange)"
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, i) => (
            <Cell
              key={i}
              fill={
                (entry.netCashChange ?? 0) >= 0
                  ? "hsl(var(--chart-1))"
                  : "hsl(var(--chart-4))"
              }
              radius={[4, 4, 0, 0] as unknown as number}
            />
          ))}
        </Bar>
        <Line
          type="monotone"
          dataKey="cumulativeCashChange"
          stroke="hsl(var(--chart-2))"
          strokeWidth={2}
          dot={{
            r: 3,
            fill: "hsl(var(--chart-2))",
            strokeWidth: 2,
            stroke: "hsl(var(--background))",
          }}
          activeDot={{ r: 5, strokeWidth: 2 }}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
